import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ShoppingBag, Users, TrendingUp, AlertTriangle,
  Package, DollarSign, ArrowRight,
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import api from '@/services/api';
import { formatCurrency, formatDate } from '@/utils';
import { OrderStatusBadge } from '@/components/common/OrderStatusBadge';
import { Skeleton } from '@/components/common/Skeleton';

interface DashboardData {
  totalOrders: number;
  ordersToday: number;
  totalRevenue: number;
  newUsersToday: number;
  lowStockProducts: { id: string; name: string; stock: number; sku: string }[];
  recentOrders: Array<{ id: string; orderNumber: string; status: string; total: number; createdAt: string; user: { name: string } }>;
  topProducts: Array<{ productId: string; _sum: { quantity: number }; product?: { name: string; images: string[] } }>;
}

export const AdminDashboard = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [analytics, setAnalytics] = useState<{ date: string; revenue: number }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/admin/dashboard'),
      api.get('/admin/analytics?days=30'),
    ]).then(([d, a]) => {
      setData(d.data.data);
      setAnalytics(a.data.data);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {Array(4).fill(0).map((_, i) => <Skeleton key={i} className="h-32" />)}
      </div>
      <Skeleton className="h-64" />
    </div>
  );

  const kpis = [
    { label: 'Total Revenue', value: formatCurrency(data?.totalRevenue || 0), icon: DollarSign, color: 'text-primary-600', bg: 'bg-primary-50' },
    { label: 'Total Orders', value: data?.totalOrders || 0, sub: `${data?.ordersToday || 0} today`, icon: ShoppingBag, color: 'text-blue-600', bg: 'bg-blue-50' },
    { label: 'New Users Today', value: data?.newUsersToday || 0, icon: Users, color: 'text-purple-600', bg: 'bg-purple-50' },
    { label: 'Low Stock Items', value: data?.lowStockProducts.length || 0, icon: AlertTriangle, color: 'text-orange-600', bg: 'bg-orange-50' },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading font-bold text-2xl text-gray-800">Admin Dashboard</h1>
          <p className="text-gray-500 text-sm">Milan Medical — Nadiad, Gujarat</p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {kpis.map((kpi, i) => (
          <div key={i} className="bg-white rounded-card shadow-card p-5">
            <div className="flex items-start justify-between mb-3">
              <div className={`w-10 h-10 rounded-xl ${kpi.bg} flex items-center justify-center`}>
                <kpi.icon className={`h-5 w-5 ${kpi.color}`} />
              </div>
            </div>
            <p className="font-heading font-bold text-2xl text-gray-800">{kpi.value}</p>
            <p className="text-sm text-gray-500 mt-0.5">{kpi.label}</p>
            {kpi.sub && <p className="text-xs text-gray-400">{kpi.sub}</p>}
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        {/* Revenue Chart */}
        <div className="lg:col-span-2 bg-white rounded-card shadow-card p-6">
          <h2 className="font-heading font-semibold text-gray-800 mb-4">Revenue (Last 30 Days)</h2>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={analytics}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={d => d.slice(5)} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `₹${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v: number) => formatCurrency(v)} labelFormatter={l => `Date: ${l}`} />
              <Line type="monotone" dataKey="revenue" stroke="#0D7377" strokeWidth={2.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Low Stock Alert */}
        <div className="bg-white rounded-card shadow-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-semibold text-gray-800">Low Stock Alert</h2>
            <Link to="/admin/inventory" className="text-xs text-primary-600 font-semibold">View All</Link>
          </div>
          {data?.lowStockProducts.length === 0 ? (
            <p className="text-gray-400 text-sm">All products are well-stocked.</p>
          ) : (
            <div className="space-y-3">
              {data?.lowStockProducts.slice(0, 8).map(p => (
                <div key={p.id} className="flex items-center justify-between">
                  <p className="text-sm text-gray-700 truncate flex-1 mr-2">{p.name}</p>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${p.stock === 0 ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'}`}>
                    {p.stock} left
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent Orders */}
      <div className="bg-white rounded-card shadow-card p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading font-semibold text-gray-800">Recent Orders</h2>
          <Link to="/admin/orders" className="text-sm text-primary-600 font-semibold flex items-center gap-1">
            View All <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left pb-3 text-gray-400 font-medium">Order #</th>
                <th className="text-left pb-3 text-gray-400 font-medium">Customer</th>
                <th className="text-left pb-3 text-gray-400 font-medium">Date</th>
                <th className="text-left pb-3 text-gray-400 font-medium">Amount</th>
                <th className="text-left pb-3 text-gray-400 font-medium">Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data?.recentOrders.map(order => (
                <tr key={order.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                  <td className="py-3 font-medium text-primary-600">#{order.orderNumber}</td>
                  <td className="py-3 text-gray-700">{order.user.name}</td>
                  <td className="py-3 text-gray-400">{formatDate(order.createdAt)}</td>
                  <td className="py-3 font-semibold">{formatCurrency(Number(order.total))}</td>
                  <td className="py-3"><OrderStatusBadge status={order.status as import('@/types').OrderStatus} /></td>
                  <td className="py-3">
                    <Link to={`/orders/${order.id}`} className="text-primary-600 hover:text-primary-700">
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
